package com.devi3east.bashbuddy.account.profile.ui

import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.consumeWindowInsets
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.layout.wrapContentWidth
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.devi3east.bashbuddy.account.components.AccountCard
import com.devi3east.bashbuddy.account.components.SelectOrganizationPromotionCard
import com.devi3east.bashbuddy.account.profile.domain.models.Profile
import com.devi3east.bashbuddy.auth.ui.AuthUiState
import com.devi3east.bashbuddy.common.domain.models.Address
import com.devi3east.bashbuddy.dashboard.ui.components.dialogs.ConfirmDeleteDialog
import com.devi3east.bashbuddy.features.app.data.AppState
import com.devi3east.bashbuddy.features.common.util.PhoneNumberTransformation
import kotlinx.coroutines.launch

@Composable
fun ProfileRoute(
    appState: AppState,
    profile: Profile
) {
    val uiState by appState.authViewModel.uiState.collectAsState()
    val coroutineScope = rememberCoroutineScope()

    LaunchedEffect(Unit) {
        appState.authViewModel.fetchProfile(profile._id)
        appState.authViewModel.loadOrganizations()
    }

    ProfileScreen(
        appState = appState,
        uiState = uiState,
        onSignOut = {
            appState.authViewModel.signOut()
        },
        onUpdateProfile = { updatedProfile ->
            coroutineScope.launch {
                appState.authViewModel.updateProfile(updatedProfile)
            }
        },
        onToggleAccount = { appState.authViewModel.toggleAccountActivation() },
        onConfirmDeleteAccount = { appState.authViewModel.deleteAccount() }
    )
}

@Composable
fun ProfileScreen(
    appState: AppState,
    uiState: AuthUiState,
    onSignOut: () -> Unit,
    onUpdateProfile: suspend (Profile) -> Unit,
    onToggleAccount: () -> Unit,
    onConfirmDeleteAccount: () -> Unit
) {

    val cardShape = RoundedCornerShape(16.dp)
    val infoInteraction = remember { MutableInteractionSource() }
    val settingsInteraction = remember { MutableInteractionSource() }
    var isEditing by remember { mutableStateOf(false) }
    var expandInfo by remember { mutableStateOf(false) }
    var expandSettings by remember { mutableStateOf(false) }
    val coroutineScope = rememberCoroutineScope()

    var isDeactivated by remember { mutableStateOf(!uiState.isActive) }
    var showDeleteDialog by remember { mutableStateOf(false) }

    val currentProfile = uiState.profile
    if (currentProfile == null) {
        if (uiState.token == null) return
        Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
            CircularProgressIndicator(color = MaterialTheme.colorScheme.primary)
        }
        return
    }

    var name by remember { mutableStateOf("") }
    var displayName by remember { mutableStateOf(currentProfile.displayName) }
    var displayNameError by remember { mutableStateOf(false) }
    var phone by remember { mutableStateOf("") }
    var addressLine1 by remember { mutableStateOf("") }
    var city by remember { mutableStateOf("") }
    var state by remember { mutableStateOf("") }
    var zipCode by remember { mutableStateOf("") }

    LaunchedEffect(currentProfile) {
        displayName = currentProfile.displayName
        name = currentProfile.name.orEmpty()
        phone = currentProfile.phone.orEmpty()
        addressLine1 = currentProfile.address?.line1.orEmpty()
        city = currentProfile.address?.city.orEmpty()
        state = currentProfile.address?.state.orEmpty()
        zipCode = currentProfile.address?.zipCode.orEmpty()
    }

    Box(
        modifier = Modifier
            .fillMaxSize()
    ) {
        Scaffold { innerPadding ->
            BoxWithConstraints(
                modifier = Modifier
                    .fillMaxSize()
                    .consumeWindowInsets(innerPadding)
            ) {
                val maxWidthDp = maxWidth
                val targetWidth = when {
                    maxWidthDp < 600.dp -> maxWidthDp
                    maxWidthDp < 900.dp -> 600.dp
                    else -> 720.dp
                }

                Column(
                    horizontalAlignment = Alignment.CenterHorizontally,
                    modifier = Modifier
                        .fillMaxSize()
                        .verticalScroll(rememberScrollState())
                        .imePadding()
                        .padding(
                            start = 16.dp,
                            end = 16.dp,
                            top = innerPadding.calculateTopPadding() + 8.dp,
                            bottom = innerPadding.calculateBottomPadding() + 16.dp
                        )
                ) {
                    Box(
                        modifier = Modifier
                            .widthIn(max = targetWidth)
                    ) {
                        Column(
                            horizontalAlignment = Alignment.CenterHorizontally,
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            Text(
                                text = currentProfile.displayName ?: "User",
                                style = MaterialTheme.typography.headlineMedium,
                                modifier = Modifier.padding(vertical = 16.dp),
                                color = MaterialTheme.colorScheme.onBackground
                            )

                            if (uiState.profile.affiliatedOrganization == null) {
                                SelectOrganizationPromotionCard(
                                    organizations = uiState.organizations,
                                    onSubmit = { selectedOrg ->
                                        coroutineScope.launch {
                                            onUpdateProfile(uiState.profile.copy(affiliatedOrganization = selectedOrg._id))
                                        }
                                    }
                                )

                                Spacer(modifier = Modifier.height(16.dp))
                            }

                            AccountCard(
                                title = "Profile Information",
                                expanded = expandInfo,
                                onToggle = { expandInfo = !expandInfo },
                                interactionSource = infoInteraction,
                                shape = cardShape
                            ) {
                                OutlinedTextField(
                                    value = displayName ?: "",
                                    onValueChange = { displayName = it.take(20) },
                                    label = { Text("Display Name") },
                                    readOnly = !isEditing,
                                    modifier = Modifier.fillMaxWidth(),
                                    singleLine = true,
                                    shape = RoundedCornerShape(12.dp)
                                )
                                if (displayNameError) {
                                    Text(
                                        modifier = Modifier.padding(top = 4.dp, start = 4.dp),
                                        text = "Display Name is required.",
                                        color = MaterialTheme.colorScheme.error,
                                        style = MaterialTheme.typography.bodySmall
                                    )
                                }
                                Spacer(Modifier.height(8.dp))
                                OutlinedTextField(
                                    value = name,
                                    onValueChange = { name = it },
                                    label = { Text("Name") },
                                    readOnly = !isEditing,
                                    modifier = Modifier.fillMaxWidth(),
                                    singleLine = true,
                                    shape = RoundedCornerShape(12.dp)
                                )
                                Spacer(Modifier.height(8.dp))
                                OutlinedTextField(
                                    value = phone,
                                    onValueChange = { phone = it.filter { char -> char.isDigit() }.take(10) },
                                    label = { Text("Phone") },
                                    keyboardOptions = KeyboardOptions.Default.copy(keyboardType = KeyboardType.Phone),
                                    visualTransformation = PhoneNumberTransformation(),
                                    readOnly = !isEditing,
                                    modifier = Modifier.fillMaxWidth(),
                                    singleLine = true,
                                    shape = RoundedCornerShape(12.dp)
                                )
                                Spacer(Modifier.height(8.dp))
                                OutlinedTextField(
                                    value = addressLine1,
                                    onValueChange = { addressLine1 = it },
                                    label = { Text("Address Line 1") },
                                    readOnly = !isEditing,
                                    modifier = Modifier.fillMaxWidth(),
                                    singleLine = true,
                                    shape = RoundedCornerShape(12.dp)
                                )
                                Spacer(Modifier.height(8.dp))
                                OutlinedTextField(
                                    value = city,
                                    onValueChange = { city = it },
                                    label = { Text("City") },
                                    readOnly = !isEditing,
                                    modifier = Modifier.fillMaxWidth(),
                                    singleLine = true,
                                    shape = RoundedCornerShape(12.dp)
                                )
                                Spacer(Modifier.height(8.dp))
                                OutlinedTextField(
                                    value = state,
                                    onValueChange = { state = it.take(2) },
                                    label = { Text("State") },
                                    readOnly = !isEditing,
                                    modifier = Modifier.fillMaxWidth(),
                                    singleLine = true,
                                    shape = RoundedCornerShape(12.dp)
                                )
                                Spacer(Modifier.height(8.dp))
                                OutlinedTextField(
                                    value = zipCode,
                                    onValueChange = { zipCode = it.take(5) },
                                    label = { Text("Zip Code") },
                                    readOnly = !isEditing,
                                    modifier = Modifier.fillMaxWidth(),
                                    singleLine = true,
                                    shape = RoundedCornerShape(12.dp)
                                )

                                Spacer(Modifier.height(16.dp))
                                Row(
                                    horizontalArrangement = Arrangement.End,
                                    modifier = Modifier.fillMaxWidth()
                                ) {
                                    if (isEditing) {
                                        TextButton(
                                            onClick = {
                                                displayName = currentProfile.displayName.orEmpty()
                                                name = currentProfile.name.orEmpty()
                                                phone = currentProfile.phone.orEmpty()
                                                addressLine1 = currentProfile.address?.line1.orEmpty()
                                                city = currentProfile.address?.city.orEmpty()
                                                state = currentProfile.address?.state.orEmpty()
                                                zipCode = currentProfile.address?.zipCode.orEmpty()

                                                isEditing = false
                                                displayNameError = false
                                            }
                                        ) {
                                            Text("Cancel", color = MaterialTheme.colorScheme.onSurfaceVariant)
                                        }

                                        Spacer(Modifier.width(12.dp))

                                        Button(
                                            onClick = {
                                                if (displayName?.isBlank() == true) {
                                                    displayNameError = true
                                                    return@Button
                                                }

                                                val updatedProfile = currentProfile.copy(
                                                    displayName = displayName,
                                                    name = name,
                                                    phone = phone,
                                                    address = Address(
                                                        line1 = addressLine1,
                                                        city = city,
                                                        state = state,
                                                        zipCode = zipCode
                                                    )
                                                )

                                                coroutineScope.launch {
                                                    onUpdateProfile(updatedProfile)
                                                    appState.authViewModel.setProfile(updatedProfile)
                                                }

                                                isEditing = false
                                                displayNameError = false
                                            },
                                            shape = RoundedCornerShape(12.dp),
                                            colors = ButtonDefaults.buttonColors(
                                                containerColor = MaterialTheme.colorScheme.primary,
                                                contentColor = MaterialTheme.colorScheme.onPrimary
                                            )
                                        ) {
                                            Text("Save")
                                        }
                                    } else {
                                        Button(
                                            onClick = { isEditing = true },
                                            shape = RoundedCornerShape(12.dp),
                                            colors = ButtonDefaults.buttonColors(
                                                containerColor = MaterialTheme.colorScheme.primary,
                                                contentColor = MaterialTheme.colorScheme.onPrimary
                                            )
                                        ) {
                                            Text("Edit")
                                        }
                                    }
                                }
                            }

                            Spacer(Modifier.height(16.dp))

                            AccountCard(
                                title = "Account",
                                expanded = expandSettings,
                                onToggle = { expandSettings = !expandSettings },
                                interactionSource = settingsInteraction,
                                shape = cardShape
                            ) {
                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Button(
                                        onClick = {
                                            onToggleAccount()
                                            isDeactivated = !isDeactivated
                                        },
                                        shape = RoundedCornerShape(12.dp),
                                        colors = ButtonDefaults.buttonColors(
                                            containerColor = if (isDeactivated)
                                                MaterialTheme.colorScheme.primary
                                            else
                                                MaterialTheme.colorScheme.error
                                        ),
                                        modifier = Modifier.width(160.dp)
                                    ) {
                                        Text(if (isDeactivated) "Reactivate" else "Deactivate")
                                    }

                                    Spacer(Modifier.width(16.dp))

                                    Text(
                                        text = if (isDeactivated) "If you'd like to keep your account." else "This must be done before an account can be deleted.",
                                        style = MaterialTheme.typography.bodyMedium,
                                        modifier = Modifier
                                            .weight(1f)
                                            .wrapContentWidth(Alignment.CenterHorizontally),
                                        textAlign = TextAlign.Center
                                    )
                                }

                                Spacer(Modifier.height(16.dp))

                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Button(
                                        onClick = { showDeleteDialog = true },
                                        enabled = isDeactivated,
                                        shape = RoundedCornerShape(12.dp),
                                        colors = ButtonDefaults.buttonColors(
                                            containerColor = if (isDeactivated)
                                                MaterialTheme.colorScheme.error
                                            else
                                                MaterialTheme.colorScheme.surfaceVariant
                                        ),
                                        modifier = Modifier.width(160.dp)
                                    ) {
                                        Text(text = "Delete")
                                    }

                                    Spacer(Modifier.width(16.dp))

                                    Text(
                                        text = "Permanently remove account.",
                                        style = MaterialTheme.typography.bodyMedium,
                                        modifier = Modifier
                                            .weight(1f)
                                            .wrapContentWidth(Alignment.CenterHorizontally),
                                        textAlign = TextAlign.Center
                                    )
                                }

                                if (showDeleteDialog) {
                                    ConfirmDeleteDialog(
                                        title = "Delete Account?",
                                        message = "This action is permanent and cannot be undone. All data associated with your account will be permanently removed.",
                                        onConfirm = {
                                            showDeleteDialog = false
                                            onConfirmDeleteAccount()
                                            onSignOut()
                                        },
                                        onDismiss = { showDeleteDialog = false }
                                    )
                                }
                            }

                            Spacer(Modifier.height(24.dp))

                            Button(
                                onClick = onSignOut,
                                shape = RoundedCornerShape(12.dp),
                                colors = ButtonDefaults.buttonColors(
                                    containerColor = MaterialTheme.colorScheme.primary,
                                    contentColor = MaterialTheme.colorScheme.onPrimary
                                ),
                                modifier = Modifier.align(Alignment.CenterHorizontally)
                            ) {
                                Text(text ="Sign Out")
                            }
                        }
                    }
                }
            }
        }
    }
}